import * as React from "react";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ChargeLine } from "./types";
import Modifiers from "./Modifiers";
import DiagnosisCodes from "./DiagnosisCodes";

interface ChargeLineProps {
  index: number;
  chargeLine: ChargeLine;
  totalModifiers: number;
  totalDiagnosisCodes: number;
  onChange: (
    index: number,
    field: keyof ChargeLine,
    value: ChargeLine[keyof ChargeLine]
  ) => void;
  onRemove: (index: number) => void;
  isReadonly: boolean;
}

const NDC_CODE_MAX_LENGTH = 12;
const DIAG_CODE_MAX_LENGTH = 10;
const CPT_CODE_MAX_LENGTH = 10;

const TemplateChargeLine: React.FC<ChargeLineProps> = ({
  index,
  chargeLine,
  totalModifiers,
  totalDiagnosisCodes,
  onChange,
  onRemove,
  isReadonly,
}) => {
  const getChargeTitle = (i: number) =>
    `Charge Line ${["One", "Two", "Three", "Four", "Five", "Six"][i]}`;

  const handleArrayChange = (
    field: "modifiers" | "diagnosis_codes",
    i: number,
    value: string
  ) => {
    const updated = [...(chargeLine[field] || [])];
    updated[i] = {
      code: value,
      index: i,
      id: chargeLine[field][i]?.id || null,
    };
    onChange(index, field, updated as any);
  };

  const addArrayField = (field: "modifiers" | "diagnosis_codes") => {
    const currentArray = chargeLine[field] || [];
    const newItem = {
      code: "",
      index: currentArray.length,
      id: null,
    };
    const updated = [...currentArray, newItem];
    onChange(index, field, updated as any);
  };

  const removeArrayField = (
    field: "modifiers" | "diagnosis_codes",
    i: number
  ) => {
    const currentArray = [...(chargeLine[field] || [])];
    currentArray.splice(i, 1);

    const updated = currentArray.map((item, idx) => ({
      ...item,
      index: idx,
    }));

    onChange(index, field, updated as any);
  };

  const unitModifierOptions = [
    { value: "F2", label: "F2" },
    { value: "GR", label: "GR" },
    { value: "ME", label: "ME" },
    { value: "ML", label: "ML" },
    { value: "UN", label: "UN" },
  ];

  return (
    <Box className="charge-line-outer-section">
      <Box className="charge-line-inner-section">
        <Box className="charge-line-header-section">
          <Typography className="charge-line-header">
            {getChargeTitle(index)}
          </Typography>
          <Box>
            {index > 0 && (
              <IconButton
                onClick={() => onRemove(index)}
                sx={{ padding: "0px" }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Grid container sx={{ padding: "24px" }} wrap="nowrap">
          <Grid item xs={3.5}>
            <Grid container columnSpacing={3} rowSpacing={1}>
              <Grid item xs={6}>
                <InputLabel
                  htmlFor={`cpt_code_${index}`}
                  sx={{
                    color: "#A29D9B",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "26px",
                  }}
                >
                  CPT Code
                </InputLabel>
                <Box className="field-container" sx={{ marginLeft: "0px" }}>
                  <TextField
                    id={`cpt_code_${index}`}
                    size="small"
                    value={chargeLine?.cpt_code}
                    variant="outlined"
                    className="the-field"
                    placeholder="XXXXXXXXXX"
                    disabled={isReadonly}
                    autoComplete="off"
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      const { value } = event.target;
                      if (value.length > CPT_CODE_MAX_LENGTH) return;

                      onChange(index, "cpt_code", value);
                    }}
                    inputProps={{ maxLength: CPT_CODE_MAX_LENGTH }}
                    sx={{
                      width: "100% !important",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <InputLabel
                  htmlFor={`units_${index}`}
                  sx={{
                    color: "#A29D9B",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "26px",
                  }}
                >
                  Units
                </InputLabel>
                <Box className="field-container" sx={{ marginLeft: "0px" }}>
                  <TextField
                    id={`units_${index}`}
                    size="small"
                    value={chargeLine?.units}
                    variant="outlined"
                    className="the-field"
                    placeholder="XXXXXXX"
                    disabled={isReadonly}
                    autoComplete="off"
                    onChange={(e) => {
                      const units = e.target.value.replace(/\D/g, "");
                      onChange(index, "units", units);
                    }}
                    sx={{
                      width: "100% !important",
                    }}
                    slotProps={{ htmlInput: { inputMode: "numeric" } }}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <InputLabel
                  htmlFor={`diagnosis_codes_${index}`}
                  sx={{
                    color: "#A29D9B",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "26px",
                  }}
                >
                  Diagnosis Codes
                </InputLabel>
                <Box className="field-container" sx={{ marginLeft: "0px" }}>
                  <TextField
                    id={`diagnosis_codes_${index}`}
                    size="small"
                    value={chargeLine?.diagnosis_codes[0]?.code || ""}
                    variant="outlined"
                    className="the-field"
                    placeholder="XXXXXXX"
                    autoComplete="off"
                    disabled={isReadonly}
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      const nextValue = event.target.value.slice(
                        0,
                        DIAG_CODE_MAX_LENGTH
                      );

                      handleArrayChange("diagnosis_codes", 0, nextValue);
                    }}
                    inputProps={{ maxLength: DIAG_CODE_MAX_LENGTH }}
                    sx={{
                      width: "100% !important",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item sx={{ flex: "0 0 1px", mx: "16px", alignSelf: "stretch" }}>
            <Box sx={{ width: "1px", height: "100%", bgcolor: "#D9D9D9" }} />
          </Grid>

          <Grid item xs={5}>
            <Grid container columnSpacing={2}>
              <Grid item xs={6}>
                <Modifiers
                  modifiers={chargeLine?.modifiers}
                  onChange={(i, val) => handleArrayChange("modifiers", i, val)}
                  onAdd={() => addArrayField("modifiers")}
                  onRemove={(i) => removeArrayField("modifiers", i)}
                  totalModifiers={totalModifiers}
                  isReadonly={isReadonly}
                />
              </Grid>
              <Grid item xs={6}>
                <DiagnosisCodes
                  diagnosisCodes={chargeLine?.diagnosis_codes.slice(1)}
                  onChange={(i, val) =>
                    handleArrayChange("diagnosis_codes", i, val)
                  }
                  onAdd={() => addArrayField("diagnosis_codes")}
                  onRemove={(i) => removeArrayField("diagnosis_codes", i)}
                  totalDiagnosisCodes={totalDiagnosisCodes}
                  isReadonly={isReadonly}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item sx={{ flex: "0 0 1px", mx: "16px", alignSelf: "stretch" }}>
            <Box sx={{ width: "1px", height: "100%", bgcolor: "#D9D9D9" }} />
          </Grid>

          <Grid item xs={3.5}>
            <Grid container columnSpacing={2}>
              <Grid item xs={6}>
                <InputLabel
                  htmlFor={`ndc_code_${index}`}
                  sx={{
                    color: "#A29D9B",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "26px",
                  }}
                >
                  NDC Code
                </InputLabel>

                <Box className="field-container" sx={{ marginLeft: "0px" }}>
                  <TextField
                    id={`ncd_code_${index}`}
                    size="small"
                    value={chargeLine?.ndc_code ?? ""}
                    variant="outlined"
                    className="the-field"
                    placeholder="XXXXXXXXXXX"
                    autoComplete="off"
                    disabled={isReadonly}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const rawValue = e.target.value;
                      const sanitizedValue = rawValue.replace(/\D/g, "").slice(0, NDC_CODE_MAX_LENGTH);
                      onChange(index, "ndc_code", sanitizedValue);
                    }}
                    sx={{
                      width: "100% !important",
                    }}
                    slotProps={{
                      htmlInput: { 
                        inputMode: "numeric", 
                        maxLength: NDC_CODE_MAX_LENGTH 
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <InputLabel
                  htmlFor={`unit_modifier_${index}`}
                  sx={{
                    color: "#A29D9B",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "26px",
                  }}
                >
                  Unit Modifier
                </InputLabel>
                <Box className="field-container" sx={{ marginLeft: "0px" }}>
                  <Select
                    id={`unit_modifier_${index}`}
                    value={chargeLine?.unit_modifier}
                    variant="outlined"
                    className="the-field"
                    displayEmpty
                    disabled={isReadonly}
                    onChange={(event) => {
                      onChange(index, "unit_modifier", event.target.value);
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <>Select Modifier</>;
                      }
                      return (
                        unitModifierOptions.find(
                          (opt) => opt.value === selected
                        )?.label || selected
                      );
                    }}
                  >
                    {unitModifierOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TemplateChargeLine;
